import React, { useState, useMemo, useCallback } from "react";
import { Button, Dropdown, Icon, Menu, Modal, Table, Tooltip, Alert } from "antd";
import { useDispatch } from "react-redux";
import moment from "moment";
import { FaUserPlus, FaExternalLinkAlt } from "react-icons/fa";

import { AppUserContainer } from "../styles";
import { UserModal } from "./UserModal";
import { CustomerPasswordModal } from "~/components/Customers/Modals/CustomerPasswordModal";
import * as customersActions from "~/_domains/customers/drawer/actions";
import { UserInviteModal } from "./UserInviteModal";
import { DefaulterNoticeCard } from "./DefaulterNoticeCard";
import { getDefaultPaginationConfig } from "~/utils/get-default-pagination-config";
import { STORAGE } from "~/constants/storage.constants";
import { useAppContext } from "~/AppContext";

export const AppUsers = ({ customer }) => {
  const dispatch = useDispatch();
  const { setOnboardingTaskAsDone } = useAppContext();
  const [users, setUsers] = useState(customer.app_users || []);
  const [userModal, setUserModal] = useState({ isVisible: false, data: null });
  const [userPasswordModal, setUserPasswordModal] = useState({ isVisible: false, data: null });
  const [userInviteModal, setUserInviteModal] = useState({ isVisible: false });
  const [impersonateAccessLoading, setImpersonateAccessLoading] = useState(false);

  // Check if customer is inactive or archived (but not deleted)
  const isCustomerInactive = customer.deleted_at ? false : (customer.inactived_at !== null || !customer.enabled);

  const showModal = useCallback((setter, data = null) => setter({ isVisible: true, data }), []);
  const hideModal = useCallback((setter) => setter({ isVisible: false, data: null }), []);

  const impersonateAccess = useCallback((customer) => {
    setImpersonateAccessLoading(true);
    const data = {
      customer_id: customer.id,
    };

    dispatch(customersActions.impersonateAccess(customer.id, data))
      .then((response) => {
        const apiToken = response.payload.data.data.api_token;

        const customerAppLink = `http://${
          process.env.REACT_APP_CUSTOMER_DOMAIN
        }/?token=${apiToken}`;

        const newWindow = window.open(customerAppLink, '_blank');
        if (newWindow) {
          newWindow.focus();
        }
        setOnboardingTaskAsDone('y5z6a7');
      })
      .finally(() => {
        setImpersonateAccessLoading(false);
      });
  }, [dispatch, setOnboardingTaskAsDone]);

  const deleteAppUser = useCallback(
    (params) => {
      Modal.confirm({
        title: "Você realmente deseja remover este usuário?",
        content: "Esta ação não poderá ser desfeita.",
        okText: "Sim",
        okType: "danger",
        cancelText: "Cancelar",
        onOk: async () => {
          await dispatch(customersActions.deleteAppUser(params));
          setUsers((prevUsers) => prevUsers.filter((user) => user.id !== params.appUserId));
        },
      });
    },
    [dispatch]
  );

  const resendInvite = useCallback(
    (params) => {
      Modal.confirm({
        title: "Você realmente deseja reenviar o convite para este usuário?",
        content: "O usuário receberá um novo e-mail de convite.",
        okText: "Sim",
        cancelText: "Cancelar",
        onOk: async () => {
          await dispatch(customersActions.resendInvite(params));
          Modal.success({ title: "Convite reenviado com sucesso!" });
        },
      });
    },
    [dispatch]
  );

  const columns = useMemo(
    () => [
      {
        title: "Nome",
        key: "name",
        dataIndex: "name",
      },
      {
        title: "E-mail",
        key: "email",
        dataIndex: "email",
      },
      {
        title: "Último acesso",
        key: "last_access_at",
        dataIndex: "last_access_at",
        render: (lastAccessAt) =>
          lastAccessAt ? (
            <Tooltip title={moment(lastAccessAt).format("DD/MM/YYYY LTS")}>
              {moment(lastAccessAt).fromNow()}
            </Tooltip>
          ) : null,
      },
      {
        align: "center",
        key: "actions",
        width: 80,
        render: (_, appUser) => (
          <Dropdown
            overlay={
              <Menu>
                {appUser.last_access_at === null && (
                  <Menu.Item
                    key="4"
                    onClick={() =>
                      resendInvite({ customerId: customer.id, appUserId: appUser.id })
                    }
                  >
                    <Icon type="mail" /> Reenviar convite
                  </Menu.Item>
                )}
                <Menu.Item key="1" onClick={() => showModal(setUserModal, appUser)}>
                  <Icon type="edit" /> Editar
                </Menu.Item>
                <Menu.Item key="2" onClick={() => showModal(setUserPasswordModal, appUser)}>
                  <Icon type="lock" /> Gerar nova senha
                </Menu.Item>
                <Menu.Divider />
                <Menu.Item
                  key="3"
                  onClick={() =>
                    deleteAppUser({ customerId: customer.id, appUserId: appUser.id })
                  }
                >
                  <Icon type="delete" /> Excluir
                </Menu.Item>
              </Menu>
            }
            trigger={["click"]}
          >
            <Button type="link">
              Ações <Icon type="down" />
            </Button>
          </Dropdown>
        ),
      },
    ],
    [showModal, deleteAppUser, resendInvite, customer.id]
  );

  return (
    <AppUserContainer>
      <DefaulterNoticeCard customerId={customer.id} />
      {isCustomerInactive && (
        <Alert
          message="Cliente Inativo"
          description="Este cliente está inativo ou arquivado. As funcionalidades de usuários estão bloqueadas e o acesso do cliente ao painel também está suspenso."
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}
      <div 
        style={{ 
          display: 'flex', 
          justifyContent: 'flex-end', 
          gap: 10, 
          marginBottom: 10,
          opacity: isCustomerInactive ? 0.5 : 1,
          pointerEvents: isCustomerInactive ? 'none' : 'auto'
        }}
      >
        <Button 
          type="primary" 
          onClick={() => showModal(setUserInviteModal)}
          disabled={isCustomerInactive}
        >
          <FaUserPlus style={{ marginRight: 8 }} /> Convidar usuário
        </Button>
        <Button 
          type="default" 
          onClick={() => impersonateAccess(customer)}
          loading={impersonateAccessLoading}
          disabled={isCustomerInactive}
        >
          <FaExternalLinkAlt style={{ marginRight: 8 }} /> 
          {impersonateAccessLoading ? 'Gerando acesso...' : 'Painel do cliente'}
        </Button>
      </div>
      <div 
        style={{
          opacity: isCustomerInactive ? 0.5 : 1,
          pointerEvents: isCustomerInactive ? 'none' : 'auto',
          position: 'relative'
        }}
      >
        {isCustomerInactive && (
          <div 
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.7)',
              zIndex: 10,
              cursor: 'not-allowed'
            }}
          />
        )}
        <Table
          dataSource={users}
          columns={columns}
          size="small"
          rowKey="id"
          pagination={getDefaultPaginationConfig({
            storageKey: STORAGE.defaultPageSize.customersAppUsers,
          })}
        />
      </div>
      <UserModal
        isVisible={userModal.isVisible}
        data={userModal.data}
        hideModal={() => hideModal(setUserModal)}
        setUsers={setUsers}
        customer={customer}
        users={users}
      />
      <UserInviteModal
        isVisible={userInviteModal.isVisible}
        hideModal={() => hideModal(setUserInviteModal)}
        setUsers={setUsers}
        customer={customer}
      />
      <CustomerPasswordModal
        isOpen={userPasswordModal.isVisible}
        onClose={() => hideModal(setUserPasswordModal)}
        appUser={userPasswordModal.data}
        customer={customer}
      />
    </AppUserContainer>
  );
};
